const TRACE_KEYS = ["schema", "authority", "sample_count", "maximum_residual", "seams"] as const;
const SEAM_KEYS = [
  "side",
  "previous_points_m",
  "predicted_points_m",
  "corrected_points_m",
  "uncertainty_radius_m",
] as const;
const POINT_LIMIT_M = 1_000_000;

export type LoomBooleanConstructionPoint = readonly [number, number, number];

export interface LoomBooleanConstructionSeam {
  side: "starboard" | "port";
  previous_points_m: readonly LoomBooleanConstructionPoint[];
  predicted_points_m: readonly LoomBooleanConstructionPoint[];
  corrected_points_m: readonly LoomBooleanConstructionPoint[];
  uncertainty_radius_m: readonly number[];
}

export interface LoomBooleanConstructionTrace {
  schema: "loom.boolean-construction-trace/v1";
  authority: "provisional-worker-construction/v1";
  sample_count: number;
  maximum_residual: number;
  seams: readonly [LoomBooleanConstructionSeam, LoomBooleanConstructionSeam];
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} is not an object`);
  }
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[], label: string): void {
  const actual = Object.keys(value);
  if (actual.length !== keys.length || keys.some((key) => !actual.includes(key))) {
    throw new Error(`${label} has an unsupported field set`);
  }
}

function boundedFinite(value: unknown, label: string, minimum = -POINT_LIMIT_M): number {
  if (typeof value !== "number" || !Number.isFinite(value)
      || value < minimum || value > POINT_LIMIT_M) throw new Error(`${label} is invalid`);
  return value;
}

function point(value: unknown): LoomBooleanConstructionPoint {
  if (!Array.isArray(value) || value.length !== 3) throw new Error("construction point is invalid");
  return [
    boundedFinite(value[0], "construction point"),
    boundedFinite(value[1], "construction point"),
    boundedFinite(value[2], "construction point"),
  ];
}

function points(value: unknown, sampleCount: number): readonly LoomBooleanConstructionPoint[] {
  if (!Array.isArray(value) || value.length !== sampleCount) {
    throw new Error("construction point count is invalid");
  }
  return value.map(point);
}

function seam(value: unknown, sampleCount: number): LoomBooleanConstructionSeam {
  const item = record(value, "construction seam");
  exactKeys(item, SEAM_KEYS, "construction seam");
  if (item.side !== "starboard" && item.side !== "port") throw new Error("construction seam side is invalid");
  if (!Array.isArray(item.uncertainty_radius_m) || item.uncertainty_radius_m.length !== sampleCount) {
    throw new Error("construction uncertainty count is invalid");
  }
  return {
    side: item.side,
    previous_points_m: points(item.previous_points_m, sampleCount),
    predicted_points_m: points(item.predicted_points_m, sampleCount),
    corrected_points_m: points(item.corrected_points_m, sampleCount),
    uncertainty_radius_m: item.uncertainty_radius_m.map((radius) =>
      boundedFinite(radius, "construction uncertainty", 0)),
  };
}

export function parseLoomBooleanConstructionTrace(value: unknown): LoomBooleanConstructionTrace {
  const item = record(value, "Boolean construction trace");
  exactKeys(item, TRACE_KEYS, "Boolean construction trace");
  if (item.schema !== "loom.boolean-construction-trace/v1"
      || item.authority !== "provisional-worker-construction/v1"
      || !Number.isInteger(item.sample_count) || (item.sample_count as number) < 8
      || (item.sample_count as number) > 24) throw new Error("Boolean construction trace identity is invalid");
  const sampleCount = item.sample_count as number;
  if (!Array.isArray(item.seams) || item.seams.length !== 2) throw new Error("Boolean construction seams are invalid");
  const seams = item.seams.map((entry) => seam(entry, sampleCount));
  if (seams[0]?.side !== "starboard" || seams[1]?.side !== "port") {
    throw new Error("Boolean construction seams are not canonically ordered");
  }
  return {
    schema: item.schema,
    authority: item.authority,
    sample_count: sampleCount,
    maximum_residual: boundedFinite(item.maximum_residual, "construction residual", 0),
    seams: seams as [LoomBooleanConstructionSeam, LoomBooleanConstructionSeam],
  };
}
